import { StoreOptions } from 'vuex'
import {
    ModalsStoreState,
    StoreStateModel,
    ModalStateMessage,
    ParametersStoreState,
    RootStoreState,
    EntitiesStoreState,
    IncidentsStoreState,
    LaboratoryIncidentsStoreState,
    LaboratoryStoreState
} from '@/store/root/root-store.model'
import { Nullable } from '@/shared/types/app.types'
import {
    FrontGlobalParametersDetailResponse,
    IdentifierRegexResponse,
    LaboratoryConfigurationResponse,
    UserInformationDetailResponse
} from '@/api/resources/common/initialization/common-initialization.dto'
import { merge } from 'lodash'
import { laboratoryChangedPlugin } from '@/store/root/plugins/laboratory-changed-plugin'
import { userLoggedInChangedPlugin } from '@/store/root/plugins/user-logged-in-changed-plugin'

export const initialRootStoreState =
    (): RootStoreState => ({
        searchState: null,
        currentCustomConfigGrid: null,
        userLoggedIn: false,
        loading: false,
        modules: {},
        entitiesState: {},
        modalsState: {},
        modalStateMessage: null,
        userInformation: null,
        defaultAccess: null,
        incidenceEntityId: '',
        incidents: {},
        parameters: { global: null, local: null },
        printService: false,
        printDevices: [],
        regex: [],
        laboratoriesConfiguration: [],
        labId: 0,
        ontologyParametersConfiguration: null,
        showUserDowntimeModal: false,
        showGreetMessageFlag: false,
        userLastProfileImage: null
    })

export const rootStore: StoreOptions<RootStoreState> = {
    strict: !['production', 'test'].includes(process.env.NODE_ENV),
    state: initialRootStoreState,
    getters: {
        searchState: (state: RootStoreState): any => state.searchState,
        currentCustomConfigGrid: (state: RootStoreState): any => state.currentCustomConfigGrid,
        showGreetMessageFlag: (state: RootStoreState): boolean => state.showGreetMessageFlag,
        modules: (state: RootStoreState): any => state.modules,
        entitiesState: (state: RootStoreState): EntitiesStoreState => state.entitiesState,
        modalsState: (state: RootStoreState): ModalsStoreState => state.modalsState,
        modalStateMessage: (state: RootStoreState): Nullable<ModalStateMessage> => state.modalStateMessage,
        parameters: (state: RootStoreState): Nullable<ParametersStoreState> => state.parameters,
        showUserDowntimeModal: (state: RootStoreState): boolean => state.showUserDowntimeModal,
        userLoggedIn: (state: RootStoreState): boolean => state.userLoggedIn,
        userInformation: (state: RootStoreState): Nullable<UserInformationDetailResponse> => state.userInformation,
        defaultAccess: (state: RootStoreState): any => state.defaultAccess,
        incidenceEntityId: (state: RootStoreState): string => state.incidenceEntityId,
        laboratoryIncidents: (state: RootStoreState) => (labId: number): IncidentsStoreState | undefined => state.incidents[labId],
        regex: (state: RootStoreState): IdentifierRegexResponse[] => state.regex,
        isLoading: (state: RootStoreState): boolean => state.loading,
        printServiceStatus: (state: RootStoreState): boolean => state.printService,
        printDevices: (state: RootStoreState): any[] => state.printDevices,
        laboratoriesConfiguration: (state: RootStoreState): LaboratoryConfigurationResponse[] => state.laboratoriesConfiguration,
        laboratories:
            (state: RootStoreState): LaboratoryStoreState[] =>
                state.laboratoriesConfiguration.map(laboratoryConfiguration => ({
                    ...laboratoryConfiguration.laboratory,
                    operationCodes: laboratoryConfiguration.operations.map(operation => operation.code)
                })),
        labId: (state: RootStoreState): number => state.labId,
        ontologyParametersConfiguration: (state: RootStoreState): any => state.ontologyParametersConfiguration,
        userLastProfileImage: (state: RootStoreState): any => state.userLastProfileImage
    },
    mutations: {
        resetState: (state: RootStoreState): void => { Object.assign(state, initialRootStoreState()) },
        updateSearchState: (state: RootStoreState, payload: any): void => { state.searchState = payload },
        updateCurrentCustomConfigGrid: (state: RootStoreState, payload: any): void => { state.currentCustomConfigGrid = payload },
        updateShowGreetMessageFlag: (state: RootStoreState, payload: boolean): void => { state.showGreetMessageFlag = payload },
        updateModules: (state: RootStoreState, payload: any[]): void => { state.modules = payload.map(item => Object.freeze(item)) },
        updateOntologyParametersConfiguration: (state: RootStoreState, payload: any): void => { state.ontologyParametersConfiguration = payload },
        updateEntitiesState: (state: RootStoreState, payload: EntitiesStoreState): void => { state.entitiesState = payload },
        updateModalsState: (state: RootStoreState, payload: ModalsStoreState): void => { state.modalsState = payload },
        updateModalStateMessage: (state: RootStoreState, payload: Nullable<ModalStateMessage>): void => { state.modalStateMessage = payload },
        updateGlobalParameters: (state: RootStoreState, payload: Nullable<FrontGlobalParametersDetailResponse>): void => { state.parameters.global = Object.freeze(payload) },
        updateLocalParameters: (state: RootStoreState, payload: Nullable<any>): void => { state.parameters.local = Object.freeze(payload) },
        updateShowUserDowntimeModal: (state: RootStoreState, payload: boolean): void => { state.showUserDowntimeModal = payload },
        updateUserLoggedIn: (state: RootStoreState, payload: boolean): void => { state.userLoggedIn = payload },
        updateUserInformation: (state: RootStoreState, payload: Nullable<UserInformationDetailResponse>): void => { state.userInformation = payload },
        updateDefaultAccess: (state: RootStoreState, payload: any): void => { state.defaultAccess = payload },
        updateEntityId: (state: RootStoreState, payload: string): void => { state.incidenceEntityId = payload },
        updateLaboratoryIncidents: (state: RootStoreState, payload: LaboratoryIncidentsStoreState): void => { state.incidents[payload.laboratoryId] = payload.incidents },
        updateRegex: (state: RootStoreState, payload: IdentifierRegexResponse[]): void => { state.regex = payload.map(item => Object.freeze(item)) },
        updateLoading: (state: RootStoreState, payload: boolean): void => { state.loading = payload },
        updatePrintServiceStatus: (state: RootStoreState, payload: boolean): void => { state.printService = payload },
        updatePrintDevices: (state: RootStoreState, payload: any[]): void => { state.printDevices = payload.map(item => Object.freeze(item)) },
        updateLaboratoriesConfiguration: (state: RootStoreState, payload: LaboratoryConfigurationResponse[]): void => { state.laboratoriesConfiguration = payload.map(item => Object.freeze(item)) },
        updateLabId: (state: RootStoreState, payload: number): void => { state.labId = payload },
        updateUserLastProfileImage: (state: RootStoreState, payload: any): void => { state.userLastProfileImage = Object.freeze(payload) }
    },
    actions: {
        setEntityState: ({ commit, getters }, payload: StoreStateModel): void => {
            const entitiesState: EntitiesStoreState = getters.entitiesState
            entitiesState[payload.name] = payload.state
            commit('updateEntitiesState', entitiesState)
        },
        setModalState: ({ commit, getters }, payload: StoreStateModel): void => {
            const modalsState: ModalsStoreState = getters.modalsState
            modalsState[payload.name] = merge(modalsState[payload.name], payload.state)
            commit('updateModalsState', modalsState)
        }
    },
    plugins: [
        laboratoryChangedPlugin,
        userLoggedInChangedPlugin
    ]
}
